import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MailCheck } from "lucide-react";
import AlertsPanel from "@/components/AlertsPanel";

export default function Alerts() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Email deep-link token identifies the recipient regardless of the browser session.
    const [token] = useState(() => searchParams.get("token") || "");

    useEffect(() => {
        // Strip the token from the address bar (keep it only in state) once captured.
        if (token) {
            const clean = new URLSearchParams(searchParams);
            clean.delete("token");
            const q = clean.toString();
            window.history.replaceState({}, "", `/alerts${q ? `?${q}` : ""}`);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (loading || token) return; // token mode doesn't require a session
        if (!user) navigate("/login?next=/alerts", { replace: true });
    }, [user, loading, token, navigate]);

    if (!token && (loading || !user)) return <div className="text-center text-[#a0a0a0] py-20">Loading alerts…</div>;

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-10" data-testid="alerts-page">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] mb-3">
                <ArrowLeft className="h-3 w-3" /> Back
            </Link>
            <div className="flex items-baseline justify-between mb-2">
                <div>
                    <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em]">Notifications</div>
                    <h1 className="font-display text-5xl tracking-wide">My Alerts</h1>
                </div>
            </div>
            <div className="h-px bg-[#2a2a2a] mb-6" />

            <div className="border border-[#ff4500]/30 bg-[#ff4500]/5 p-3 text-xs text-[#cfcfcf] mb-6 flex items-start gap-2">
                <MailCheck className="h-4 w-4 text-[#ff4500] mt-0.5 shrink-0" />
                <div>
                    {token
                        ? <>You're managing the alerts for the account this email link was sent to. Every time a new team or event matches one of these alerts, we'll email that account.</>
                        : <>Every time a new team or event that matches one of your alerts is added, we'll email <strong>{user?.email}</strong>. Save a new alert from the home page by clicking <strong>Save Search</strong> after picking your filters.</>}
                </div>
            </div>

            <AlertsPanel token={token} />
        </div>
    );
}
